'use client'

import { useEffect, useMemo, useRef } from 'react'
import { type CategoryDTO, type Currency, type OldProductDTO, type Option } from 'ecosystem'
import { overrideText } from 'ui/lib/overrides'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import { getProductPrice } from 'shared-utils'
import { type CurrencyOverrides } from '../../types'
import { createCategoryMap, ga4EcommerceItemFactoryFromProducts } from '../utils'
import { useGtmEvents } from './useGtmEvents'

interface UseTrackProductViewProps {
  product: OldProductDTO
  categories: Option<CategoryDTO[]>
  currency: Currency
}

export const useGtmViewProductPageTracker = ({
  product,
  categories,
  currency
}: UseTrackProductViewProps) => {
  const { trackViewProductEvent, trackViewItemEvent } = useGtmEvents()
  const overrides = useOverridesContext<keyof CurrencyOverrides>()

  const isViewProductEventTracked = useRef(false)
  const isViewItemTracked = useRef(false)

  const categoryMap = useMemo(() => createCategoryMap(categories), [categories])

  useEffect(() => {
    if (!isViewItemTracked.current) {
      isViewItemTracked.current = trackViewItemEvent({
        ecommerce: {
          currency: overrideText(currency, overrides?.currency) as Currency,
          value: getProductPrice(product),
          items: ga4EcommerceItemFactoryFromProducts([product], categoryMap)
        }
      })
    }

    if (!isViewProductEventTracked.current) {
      isViewProductEventTracked.current = trackViewProductEvent({
        product_id: product.articleNumber,
        product_name: product.name,
        price: getProductPrice(product),
        brand: product.brand?.name,
        tag: product.tags?.[0].name,
        category: product.categoryIds?.[0] ? categoryMap[product.categoryIds[0]] : '',
        in_stock: product.inStock,
        slug: product.slug,
        currency: overrideText(currency, overrides?.currency) as Currency
      })
    }
  }, [
    categories,
    categoryMap,
    currency,
    overrides?.currency,
    product,
    trackViewItemEvent,
    trackViewProductEvent
  ])
}
